export type HttpTransportRequestOptions = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD';
  headers?: Record<string, string>;
  body?: string | FormData | Blob;
  timeoutMs?: number;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
};

export type HttpTransportResponse = {
  status: number;
  ok: boolean;
  text(): Promise<string>;
  json(): Promise<unknown>;
};

export function headersInitToRecord(extra: HeadersInit | undefined): Record<string, string> {
  if (!extra) return {};
  if (Array.isArray(extra)) {
    return Object.fromEntries(extra.map(([k, v]) => [k, v]));
  }
  if (extra instanceof Headers) {
    const out: Record<string, string> = {};
    extra.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }
  return { ...extra };
}

export async function executeHttpFetch(
  url: string,
  options: HttpTransportRequestOptions,
): Promise<HttpTransportResponse> {
  const { method, headers, body, timeoutMs, signal: externalSignal } = options;

  let signal = externalSignal;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  if (timeoutMs && !externalSignal) {
    const controller = new AbortController();
    signal = controller.signal;
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }

  try {
    const response = await fetch(url, {
      method,
      headers,
      body,
      mode: 'cors',
      ...(signal ? { signal } : {}),
    });

    return {
      status: response.status,
      ok: response.ok,
      text: () => response.text(),
      json: () => response.json(),
    };
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}
